import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type {
  Meeting,
  MeetingType,
  MeetingStatus,
  MeetingAttendee,
  MeetingDecision,
  MeetingActionItem,
  MeetingKeyQuestion,
} from '@/types'
import type { MeetingsState } from './state'
import { useUsersStore } from '@/views/Users/store'

export interface CreateMeetingData {
  projectId: string
  applicationId?: string
  implementationId?: string
  title: string
  description?: string
  meetingType: MeetingType
  scheduledDate: Date
  startTime?: string
  endTime?: string
  duration?: number
  location?: string
  attendees?: MeetingAttendee[]
  agenda?: string
  tags?: string[]
}

export interface UpdateMeetingData {
  title?: string
  description?: string
  meetingType?: MeetingType
  status?: MeetingStatus
  scheduledDate?: Date
  startTime?: string
  endTime?: string
  duration?: number
  location?: string
  attendees?: MeetingAttendee[]
  agenda?: string
  notes?: string
  transcriptDocumentId?: string
  decisions?: MeetingDecision[]
  actionItems?: MeetingActionItem[]
  keyQuestions?: MeetingKeyQuestion[]
  tags?: string[]
}

// Remove undefined values recursively from an object/array (Firestore rejects undefined)
const stripUndefined = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(stripUndefined)
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)])
    )
  }
  return obj
}

export const createActions = (state: MeetingsState) => {
  const { logActivity } = useActivityLog()
  const notificationsStore = useNotificationsStore()
  let globalUnsubscribe: Unsubscribe | null = null

  const mapMeetingDoc = (d: { id: string; data: () => Record<string, unknown> }): Meeting => {
    const data = d.data()
    return {
      id: d.id,
      tenantId: data.tenantId,
      ...data,
      operatorIds: data.operatorIds || [],
      scheduledDate: (data.scheduledDate as any)?.toDate?.() || new Date(),
      attendees: (data.attendees || []),
      decisions: ((data.decisions || []) as Record<string, unknown>[]).map((dec) => ({
        ...dec,
        createdAt: (dec.createdAt as any)?.toDate?.() || new Date(),
      })),
      actionItems: ((data.actionItems || []) as Record<string, unknown>[]).map((item) => ({
        ...item,
        createdAt: (item.createdAt as any)?.toDate?.() || new Date(),
        dueDate: (item.dueDate as any)?.toDate?.() || undefined,
      })),
      keyQuestions: ((data.keyQuestions || []) as Record<string, unknown>[]).map((q) => ({
        ...q,
        createdAt: (q.createdAt as any)?.toDate?.() || new Date(),
        answer: q.answer ? {
          ...(q.answer as Record<string, unknown>),
          answeredAt: ((q.answer as Record<string, unknown>).answeredAt as any)?.toDate?.() || new Date(),
        } : undefined,
      })),
      createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
      updatedAt: (data.updatedAt as any)?.toDate?.() || new Date(),
    } as Meeting
  }

  const buildMeetingsQuery = () => {
    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
      if (authStore.user.organizationId) {
        return query(
          collection(db, 'meetings'),
          ...baseConstraints,
          where('organizationId', '==', authStore.user.organizationId),
          orderBy('scheduledDate', 'desc')
        )
      }
    } else if (authStore.user?.role === 'operator') {
      return query(
        collection(db, 'meetings'),
        ...baseConstraints,
        where('operatorIds', 'array-contains', authStore.user.id),
        orderBy('scheduledDate', 'desc')
      )
    }

    return query(collection(db, 'meetings'), ...baseConstraints, orderBy('scheduledDate', 'desc'))
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const q = buildMeetingsQuery()
    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      state.meetings = snapshot.docs.map(mapMeetingDoc)
      state.isLoading = false
    }, (error) => {
      console.error('Meetings subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchMeetings = async (projectId?: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []
      let q = query(collection(db, 'meetings'), ...baseConstraints, orderBy('scheduledDate', 'desc'))

      if (projectId) {
        q = query(
          collection(db, 'meetings'),
          ...baseConstraints,
          where('projectId', '==', projectId),
          orderBy('scheduledDate', 'desc')
        )
      } else if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        if (authStore.user.organizationId) {
          q = query(
            collection(db, 'meetings'),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('scheduledDate', 'desc')
          )
        }
      } else if (authStore.user?.role === 'operator') {
        q = query(
          collection(db, 'meetings'),
          ...baseConstraints,
          where('operatorIds', 'array-contains', authStore.user.id),
          orderBy('scheduledDate', 'desc')
        )
      }

      const snapshot = await getDocs(q)
      state.meetings = snapshot.docs.map((d) => {
        const data = d.data()
        return {
          id: d.id,
          tenantId: data.tenantId,
          ...data,
          operatorIds: data.operatorIds || [],
          scheduledDate: data.scheduledDate?.toDate?.() || new Date(),
          attendees: (data.attendees || []),
          decisions: (data.decisions || []).map((dec: Record<string, unknown>) => ({
            ...dec,
            createdAt: (dec.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
          })),
          actionItems: (data.actionItems || []).map((item: Record<string, unknown>) => ({
            ...item,
            createdAt: (item.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
            dueDate: (item.dueDate as { toDate?: () => Date })?.toDate?.() || undefined,
          })),
          keyQuestions: (data.keyQuestions || []).map((q: Record<string, unknown>) => ({
            ...q,
            createdAt: (q.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
            answer: q.answer ? {
              ...(q.answer as Record<string, unknown>),
              answeredAt: ((q.answer as Record<string, unknown>).answeredAt as { toDate?: () => Date })?.toDate?.() || new Date(),
            } : undefined,
          })),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        } as Meeting
      })
    } catch (error) {
      console.error('Error fetching meetings:', error)
      state.error = 'Error al cargar las reuniones'
    } finally {
      state.isLoading = false
    }
  }

  const fetchMeetingById = async (id: string): Promise<Meeting | null> => {
    try {
      const docRef = doc(db, 'meetings', id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        const meeting = {
          id: docSnap.id,
          tenantId: data.tenantId,
          ...data,
          operatorIds: data.operatorIds || [],
          scheduledDate: data.scheduledDate?.toDate?.() || new Date(),
          attendees: (data.attendees || []),
          decisions: (data.decisions || []).map((dec: Record<string, unknown>) => ({
            ...dec,
            createdAt: (dec.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
          })),
          actionItems: (data.actionItems || []).map((item: Record<string, unknown>) => ({
            ...item,
            createdAt: (item.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
            dueDate: (item.dueDate as { toDate?: () => Date })?.toDate?.() || undefined,
          })),
          keyQuestions: (data.keyQuestions || []).map((q: Record<string, unknown>) => ({
            ...q,
            createdAt: (q.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
            answer: q.answer ? {
              ...(q.answer as Record<string, unknown>),
              answeredAt: ((q.answer as Record<string, unknown>).answeredAt as { toDate?: () => Date })?.toDate?.() || new Date(),
            } : undefined,
          })),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        } as Meeting

        state.selectedMeeting = meeting
        return meeting
      }

      return null
    } catch (error) {
      console.error('Error fetching meeting:', error)
      state.error = 'Error al cargar la reunion'
      return null
    }
  }

  const createMeeting = async (data: CreateMeetingData): Promise<string> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      // Get project to get organizationId
      const projectRef = doc(db, 'projects', data.projectId)
      const projectSnap = await getDoc(projectRef)

      if (!projectSnap.exists()) {
        throw new Error('Proyecto no encontrado')
      }

      const projectData = projectSnap.data()

      // Heredar operadores del proyecto y auto-agregarlos como asistentes
      const inheritedOperatorIds: string[] = projectData.operatorIds || []
      const autoAttendees: MeetingAttendee[] = []

      if (inheritedOperatorIds.length > 0) {
        const usersStore = useUsersStore()
        if (usersStore.users.length === 0) await usersStore.fetchUsers()

        for (const opId of inheritedOperatorIds) {
          const user = usersStore.users.find((u: { id: string }) => u.id === opId)
          if (user) {
            const alreadyAdded = (data.attendees || []).some(a => a.userId === opId)
            if (!alreadyAdded) {
              autoAttendees.push({
                userId: user.id,
                email: user.email,
                name: user.displayName || user.email,
                role: 'required',
                rsvpStatus: 'pending',
                attended: false,
              })
            }
          }
        }
      }

      const meetingData = stripUndefined({
        ...getTenantData(),
        ...data,
        implementationId: data.implementationId,
        organizationId: projectData.organizationId,
        operatorIds: inheritedOperatorIds,
        status: 'scheduled' as MeetingStatus,
        description: data.description || '',
        attendees: [...(data.attendees || []), ...autoAttendees],
        decisions: [],
        actionItems: [],
        keyQuestions: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        updatedBy: userId,
      }) as Record<string, unknown>

      const docRef = await addDoc(collection(db, 'meetings'), meetingData)

      await logActivity(
        'meeting',
        docRef.id,
        'created',
        `Se creo la reunion "${data.title}"`
      )

      // Notify admins + attendees with userId about new meeting
      const allAttendees = [...(data.attendees || []), ...autoAttendees]
      const attendeeUserIds = allAttendees.filter(a => a.userId).map(a => a.userId!)
      await notificationsStore.notifyAdminsAndStakeholders(
        attendeeUserIds,
        'meeting_created',
        'Nueva reunion creada',
        `Se ha creado la reunion "${data.title}"`,
        'meeting',
        docRef.id
      )

      await fetchMeetings(data.projectId)

      return docRef.id
    } catch (error) {
      console.error('Error creating meeting:', error)
      state.error = 'Error al crear la reunion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateMeeting = async (id: string, data: UpdateMeetingData): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      const updateData = stripUndefined({
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      }) as Record<string, unknown>

      await updateDoc(doc(db, 'meetings', id), updateData)

      await logActivity(
        'meeting',
        id,
        data.status ? 'status_changed' : 'updated',
        data.status
          ? `Se cambio el estado de la reunion a "${data.status}"`
          : 'Se actualizo la reunion'
      )

      // Notify attendees on status change
      const existingMeeting = state.meetings.find((m) => m.id === id)
      if (data.status && existingMeeting && data.status !== existingMeeting.status) {
        const attendeeUserIds = (existingMeeting.attendees || [])
          .filter(a => a.userId)
          .map(a => a.userId!)
        await notificationsStore.notifyUsers(
          attendeeUserIds,
          'meeting_status_changed',
          'Reunión actualizada',
          `"${existingMeeting.title}" cambió a ${data.status}`,
          'meeting',
          id
        )
      }

      // Update local state
      const index = state.meetings.findIndex((m) => m.id === id)
      if (index !== -1) {
        state.meetings[index] = {
          ...state.meetings[index],
          ...data,
          updatedAt: new Date(),
        }
      }

      if (state.selectedMeeting?.id === id) {
        state.selectedMeeting = {
          ...state.selectedMeeting,
          ...data,
          updatedAt: new Date(),
        }
      }
    } catch (error) {
      console.error('Error updating meeting:', error)
      state.error = 'Error al actualizar la reunion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteMeeting = async (id: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const meeting = state.meetings.find((m) => m.id === id)

      await deleteDoc(doc(db, 'meetings', id))

      await logActivity(
        'meeting',
        id,
        'deleted',
        `Se elimino la reunion "${meeting?.title || id}"`
      )

      state.meetings = state.meetings.filter((m) => m.id !== id)

      if (state.selectedMeeting?.id === id) {
        state.selectedMeeting = null
      }
    } catch (error) {
      console.error('Error deleting meeting:', error)
      state.error = 'Error al eliminar la reunion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const setFilters = (filters: Partial<MeetingsState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const clearFilters = (): void => {
    state.filters = {
      search: '',
      status: '',
      meetingType: '',
      projectId: '',
    }
  }

  const setSelectedMeeting = (meeting: Meeting | null): void => {
    state.selectedMeeting = meeting
  }

  const assignOperatorToMeeting = async (meetingId: string, operatorId: string): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.user?.id || 'system'

      // Obtener datos del operador para crear el attendee
      const usersStore = useUsersStore()
      if (usersStore.users.length === 0) await usersStore.fetchUsers()
      const operatorUser = usersStore.users.find((u: { id: string }) => u.id === operatorId)
      if (!operatorUser) throw new Error('Operador no encontrado')

      const newAttendee: MeetingAttendee = {
        userId: operatorUser.id,
        email: operatorUser.email,
        name: operatorUser.displayName || operatorUser.email,
        role: 'required',
        rsvpStatus: 'pending',
        attended: false,
      }

      // Obtener meeting actual para verificar si ya esta en attendees
      const meetingRef = doc(db, 'meetings', meetingId)
      const meetingSnap = await getDoc(meetingRef)
      const meetingData = meetingSnap.data()
      const currentAttendees = meetingData?.attendees || []
      const alreadyAttendee = currentAttendees.some(
        (a: Record<string, unknown>) => a.userId === operatorId
      )

      const updateData: Record<string, unknown> = {
        operatorIds: arrayUnion(operatorId),
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      }
      if (!alreadyAttendee) {
        updateData.attendees = [...currentAttendees, newAttendee]
      }

      await updateDoc(meetingRef, updateData)

      // Actualizar estado local
      const m = state.meetings.find((m) => m.id === meetingId)
      if (m) {
        if (!m.operatorIds) m.operatorIds = []
        if (!m.operatorIds.includes(operatorId)) {
          m.operatorIds = [...m.operatorIds, operatorId]
        }
        if (!alreadyAttendee) {
          m.attendees = [...m.attendees, newAttendee]
        }
      }
      if (state.selectedMeeting?.id === meetingId) {
        const ops = state.selectedMeeting.operatorIds || []
        const atts = state.selectedMeeting.attendees || []
        state.selectedMeeting = {
          ...state.selectedMeeting,
          operatorIds: ops.includes(operatorId) ? ops : [...ops, operatorId],
          attendees: alreadyAttendee ? atts : [...atts, newAttendee],
        }
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al asignar operador'
      throw error
    }
  }

  const unassignOperatorFromMeeting = async (meetingId: string, operatorId: string): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.user?.id || 'system'

      // Obtener meeting actual para filtrar attendees
      const meetingRef = doc(db, 'meetings', meetingId)
      const meetingSnap = await getDoc(meetingRef)
      const meetingData = meetingSnap.data()
      const currentAttendees = meetingData?.attendees || []

      // Remover de attendees el que tenga userId === operatorId
      const filteredAttendees = currentAttendees.filter(
        (a: Record<string, unknown>) => a.userId !== operatorId
      )

      await updateDoc(meetingRef, {
        operatorIds: arrayRemove(operatorId),
        attendees: filteredAttendees,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      // Actualizar estado local
      const m = state.meetings.find((m) => m.id === meetingId)
      if (m) {
        if (m.operatorIds) {
          m.operatorIds = m.operatorIds.filter((id) => id !== operatorId)
        }
        m.attendees = m.attendees.filter((a) => a.userId !== operatorId)
      }
      if (state.selectedMeeting?.id === meetingId) {
        state.selectedMeeting = {
          ...state.selectedMeeting,
          operatorIds: (state.selectedMeeting.operatorIds || []).filter((id) => id !== operatorId),
          attendees: state.selectedMeeting.attendees.filter((a) => a.userId !== operatorId),
        }
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al desasignar operador'
      throw error
    }
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchMeetings,
    fetchMeetingById,
    createMeeting,
    updateMeeting,
    deleteMeeting,
    assignOperatorToMeeting,
    unassignOperatorFromMeeting,
    setFilters,
    clearFilters,
    setSelectedMeeting,
  }
}
